import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Chargement, EnTetePage, Erreur, Infobulle, Onglets } from '../components/Elements';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useAnnee } from '../hooks/useAnnee';
import { FormulaireCreneau, FormulaireDuplication } from './FormulaireCreneau';
import { CommunicationGroupee } from '../components/CommunicationGroupee';
import { IconePresences } from '../components/IconePresences';
import { formaterDuree, formaterHeure, formaterPlage, libelleJour } from '../lib/format';
import type { Activite, Conflit, Creneau, Lieu, Personnel } from '../types';

type Regroupement = 'lieu' | 'professeur' | 'activite';

/**
 * CDC M4 : grille hebdomadaire par professeur, avec vues alternatives par
 * activité et par lieu — les trois vues du classeur. Le professeur ouvre
 * (2026-09-29) : c'est la lecture qu'on fait le plus souvent.
 */
export function PlanningPage() {
  const { entiteActive, peut } = useAuth();
  const [regroupement, setRegroupement] = useState<Regroupement>('professeur');
  const [edition, setEdition] = useState<{ ouverte: boolean; creneau: Creneau | null }>({
    ouverte: false,
    creneau: null,
  });
  // « Ecrire aux eleves » d'un creneau, depuis le menu de sa fiche (2026-10-08).
  const [ecrire, setEcrire] = useState<Creneau | null>(null);
  const [duplication, setDuplication] = useState(false);
  // Un filtre PAR ONGLET : la dimension change avec l'onglet, la
  // selection aussi. Revenir sur « par lieu » retrouve la sienne.
  // Vide = TOUT — un filtre neuf ne cache rien.
  const [selection, setSelection] = useState<Record<Regroupement, string[]>>({
    lieu: [],
    professeur: [],
    activite: [],
  });

  const cle = entiteActive?.id ?? '';
  const annee = useAnnee();
  // Referentiel annuel : les activites de l'annee affichee seulement.
  const activites = useRessource(
    () =>
      annee.anneeId
        ? api.get<Activite[]>('/api/activites', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [cle, annee.anneeId],
  );
  const personnel = useRessource(() => api.get<Personnel[]>('/api/personnel'), [cle]);
  const lieux = useRessource(() => api.get<Lieu[]>('/api/lieux'), [cle]);

  const creneaux = useRessource(
    () =>
      annee.anneeId
        ? api.get<Creneau[]>('/api/creneaux', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [cle, annee.anneeId],
  );

  const controle = useRessource(
    () =>
      annee.anneeId
        ? api.get<{
            conflits: Conflit[];
            satures: { id: string; libelle: string; effectif: number; capacite_max: number }[];
          }>('/api/creneaux/conflits', { anneeId: annee.anneeId })
        : Promise.resolve({ conflits: [], satures: [] }),
    [cle, annee.anneeId],
  );

  // Les creneaux qui SE CHEVAUCHENT, des deux cotes du conflit : c'est ce
  // qui colore leur carte en rouge. L'encart du haut les nomme, mais on
  // les cherche dans la grille — il faut donc les y voir.
  const enConflit = new Set(
    (controle.donnees?.conflits ?? []).flatMap((c) => [c.creneau_a_id, c.creneau_b_id]),
  );

  // RG-27 : une annee cloturee est figee, l'ecran n'y propose plus d'ecrire.
  const peutEcrire = annee.modifiable;

  if (annee.chargement) return <Chargement />;

  if (!annee.annee) {
    return (
      <>
        <EnTetePage titre="Planning" />
        <div className="carte p-6 text-sm text-encre-500">
          Aucune année scolaire sur cette entité.
        </div>
      </>
    );
  }

  const liste = creneaux.donnees ?? [];

  const cleDeRegroupement = (creneau: Creneau): string => {
    if (regroupement === 'lieu') return creneau.lieu_code ?? '(sans lieu)';
    // Regroupement sur le PRINCIPAL : un atelier tenu a deux appartient
    // a la ligne de celui qui le porte, l'assistant s'affichant dans la
    // carte. L'y ranger deux fois doublerait les effectifs affiches.
    if (regroupement === 'professeur')
      return creneau.encadrants[0]?.prenom_usuel ?? '(sans professeur)';
    return creneau.activite_libelle;
  };

  const toutesValeurs = [...new Set(liste.map(cleDeRegroupement))].sort();
  const choisis = selection[regroupement];
  const retenus = choisis.length === 0 ? toutesValeurs : choisis;

  // Tout ce qui suit se lit sur la liste FILTREE : les colonnes de jours,
  // les totaux de l'en-tete et la grille. Un filtre qui ne changerait que
  // les lignes laisserait un total qui ne correspond a rien d'affiche.
  const affichee = liste.filter((c) => retenus.includes(cleDeRegroupement(c)));

  const groupes = [...new Set(affichee.map(cleDeRegroupement))].sort();
  const jours = [1, 2, 3, 4, 5, 6, 7].filter((jour) => affichee.some((c) => c.jour === jour));

  const basculer = (valeur: string) => {
    const actuels = selection[regroupement];
    const suite = actuels.includes(valeur)
      ? actuels.filter((v) => v !== valeur)
      : [...actuels, valeur];
    setSelection({ ...selection, [regroupement]: suite });
  };

  return (
    <>
      {/* Ni compteurs ni chiffre d'affaire en tete (2026-09-29) : la grille
          se lit, elle ne se totalise pas. La reprise d'une annee reste sur
          l'ecran Creneaux, qui est celui du parametrage. */}
      <EnTetePage
        titre="Planning"
        actions={
          <>
            <button type="button" className="bouton-secondaire" onClick={() => window.print()}>
              Imprimer
            </button>
            {peut('creneau.creer') && peutEcrire && (
              <button
                type="button"
                className="bouton-principal"
                onClick={() => setEdition({ ouverte: true, creneau: null })}
              >
                Nouveau créneau
              </button>
            )}
          </>
        }
      />

      {!peutEcrire && <BandeauAnneeFigee annee={annee.annee} />}

      {liste.length === 0 && !creneaux.chargement && (
        <div className="carte mb-6 p-6 text-sm text-encre-500">
          Aucun créneau sur {annee.annee.libelle}. C’est l’opération de début d’année :{' '}
          {peut('creneau.creer') && peutEcrire ? (
            <>
              créez-les un par un, ou{' '}
              <button type="button" className="underline" onClick={() => setDuplication(true)}>
                reprenez la grille d’une année précédente
              </button>
              .
            </>
          ) : (
            'un gestionnaire doit les créer.'
          )}
        </div>
      )}

      {/* Les encarts qui listaient conflits et depassements en tete de page
          ont ete retires (2026-09-29) : la couleur des cartes les porte
          deja, et la legende, remontee ici, dit comment la lire. Le
          controle reste interroge — c'est lui qui colore en rouge. */}
      <Legende />

      <Onglets
        onglets={[
          { cle: 'professeur', libelle: 'Par prof' },
          { cle: 'activite', libelle: 'Par activité' },
          { cle: 'lieu', libelle: 'Par lieu' },
        ]}
        actif={regroupement}
        onChanger={(valeur) => setRegroupement(valeur as Regroupement)}
      />

      {/* Le filtre porte la DIMENSION de l'onglet : les lieux sous « par
          lieu », les professeurs sous « par professeur ». « Tout » n'est
          pas une valeur de plus — c'est l'absence de selection. */}
      {toutesValeurs.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSelection({ ...selection, [regroupement]: [] })}
            className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
              choisis.length === 0
                ? 'border-encre-800 bg-encre-800 text-white'
                : 'border-encre-200 text-encre-600 hover:border-encre-400'
            }`}
          >
            Tout ({toutesValeurs.length})
          </button>
          {toutesValeurs.map((valeur) => (
            <button
              key={valeur}
              type="button"
              onClick={() => basculer(valeur)}
              aria-pressed={choisis.includes(valeur)}
              className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
                choisis.includes(valeur)
                  ? 'border-encre-800 bg-encre-800 text-white'
                  : 'border-encre-200 text-encre-600 hover:border-encre-400'
              }`}
            >
              {valeur}
            </button>
          ))}
        </div>
      )}

      {creneaux.erreur && <Erreur message={creneaux.erreur} onReessayer={creneaux.recharger} />}
      {creneaux.chargement ? (
        <Chargement />
      ) : (
        // `lg:overflow-x-visible` : meme raison que dans `Tableau` — un
        // conteneur de defilement empeche l'en-tete de se figer sur la
        // page. La table fait 56rem, elle tient donc sans defilement des
        // que l'ecran est large.
        <div className="carte overflow-x-auto lg:overflow-x-visible">
          {/* `table-fixed` + une largeur par colonne : sans elle, un jour
              charge etirait sa colonne et les autres se tassaient. Les
              jours se partagent la place a parts egales, la colonne de
              gauche garde sa largeur propre. */}
          <table className="w-full min-w-[56rem] table-fixed border-collapse text-sm">
            <thead>
              <tr className="bg-encre-50">
                <th
                  // Figee sur les DEUX axes : a gauche pour que la colonne
                  // de regroupement reste lisible quand on defile en
                  // largeur, en haut pour que les jours restent nommes
                  // quand on defile en longueur. `z-30` la place au-dessus
                  // des autres cellules d'en-tete, qui n'en figent qu'un.
                  // Deux liseres, bas et droit, en ombre interieure :
                  // voir la note du composant `Tableau`.
                  className="sticky left-0 z-30 w-40 bg-encre-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-encre-500 shadow-[inset_-1px_-1px_0_#c5c7cc] after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:from-black/10 after:to-transparent after:content-['']"
                  style={{ top: 'var(--hauteur-bandeau, 63px)' }}
                >
                  {regroupement === 'lieu'
                    ? 'Lieu'
                    : regroupement === 'professeur'
                      ? 'Professeur'
                      : 'Activité'}
                </th>
                {jours.map((jour) => (
                  <th
                    key={jour}
                    style={{ width: `${90 / jours.length}%`, top: 'var(--hauteur-bandeau, 63px)' }}
                    // `bg-encre-50` sur la CELLULE : le fond de la ligne
                    // ne suit pas une cellule figee, qui laisserait
                    // paraitre les creneaux au travers.
                    className="sticky z-20 bg-encre-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-encre-500 shadow-[inset_0_-1px_0_#c5c7cc] after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:from-black/10 after:to-transparent after:content-['']"
                  >
                    {libelleJour(jour)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupes.map((groupe) => (
                <tr key={groupe} className="align-top">
                  <th className="sticky left-0 z-10 border-b border-r border-encre-200 bg-white px-3 py-2 text-left font-medium">
                    {groupe}
                  </th>
                  {jours.map((jour) => {
                    const cellules = affichee
                      .filter((c) => cleDeRegroupement(c) === groupe && c.jour === jour)
                      .sort((a, b) => a.heure_debut.localeCompare(b.heure_debut));

                    return (
                      <td key={jour} className="border-b border-encre-100 p-1.5">
                        <div className="space-y-1">
                          {cellules.map((creneau) => (
                            <CarteCreneau
                              key={creneau.id}
                              creneau={creneau}
                              regroupement={regroupement}
                              enConflit={enConflit.has(creneau.id)}
                              onModifier={
                                peut('creneau.modifier') && peutEcrire
                                  ? () => setEdition({ ouverte: true, creneau })
                                  : undefined
                              }
                              // L'icone Presences (2026-10-13) : sur les seuls creneaux
                              // dont le type suit les presences, a qui peut les lire.
                              presences={creneau.suivi_presence !== 'aucun' && peut('presence.lire')}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CommunicationGroupee
        ouverte={ecrire !== null}
        onFermer={() => setEcrire(null)}
        source={ecrire ? { creneauId: ecrire.id } : null}
        origine={
          ecrire
            ? `${ecrire.activite_libelle} · ${libelleJour(ecrire.jour)} ${formaterHeure(ecrire.heure_debut)} — les élèves inscrits et leurs contacts.`
            : ''
        }
      />

      <FormulaireCreneau
        onEcrire={setEcrire}
        ouverte={edition.ouverte}
        anneeId={annee.anneeId}
        anneeLibelle={annee.annee.libelle}
        creneau={edition.creneau}
        activites={activites.donnees ?? []}
        personnel={personnel.donnees ?? []}
        lieux={lieux.donnees ?? []}
        onFermer={() => setEdition({ ouverte: false, creneau: null })}
        onEnregistre={() => {
          setEdition({ ouverte: false, creneau: null });
          creneaux.recharger();
          controle.recharger();
        }}
        // Fermer un creneau depuis la grille : il en disparait au
        // rechargement, et se retrouve dans la liste des creneaux.
        onChangerStatut={
          peutEcrire
            ? async (creneau, actif) => {
                setEdition({ ouverte: false, creneau: null });
                try {
                  await api.patch(`/api/creneaux/${creneau.id}/statut`, { actif });
                  creneaux.recharger();
                  controle.recharger();
                } catch (cause) {
                  window.alert(
                    cause instanceof ErreurApi ? cause.message : 'Changement impossible.',
                  );
                }
              }
            : undefined
        }
      />

      <FormulaireDuplication
        ouverte={duplication}
        anneeCibleId={annee.anneeId}
        anneeCibleLibelle={annee.annee.libelle}
        annees={annee.annees}
        onFermer={() => setDuplication(false)}
        onEnregistre={(bilan) => {
          setDuplication(false);
          creneaux.recharger();
          controle.recharger();
          window.alert(
            `${bilan.crees} créneau(x) repris, ${bilan.ignores} ignoré(s) — déjà présents ou activité désactivée.`,
          );
        }}
      />
    </>
  );
}

/**
 * La legende du code couleur, AU-DESSUS de la grille (2026-09-29) : on la
 * lit avant les cartes, pas apres avoir fait defiler cent soixante
 * creneaux. Elle remplace les encarts de conflits et de depassements.
 */
function Legende() {
  return (
    <p className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-encre-400">
      <span>
        <span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border border-emerald-400 bg-emerald-50 align-middle" />
        Places disponibles.
      </span>
      <span>
        <span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border border-encre-200 bg-white align-middle" />
        Complet.
      </span>
      <span>
        <span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border border-yellow-400 bg-yellow-50 align-middle" />
        Au-delà de sa capacité.
      </span>
      <span>
        <span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border border-red-400 bg-red-50 align-middle" />
        En conflit de planning.
      </span>
      <span>
        <span className="mr-1 rounded border border-blue-400 bg-white px-1 text-[10px] font-medium text-blue-700 align-middle">
          réservé
        </span>
        Cours individuel dont l’élève n’a encore rien réglé.
      </span>
      <span>
        <span className="mr-1 rounded border border-blue-400 bg-white px-1 text-[10px] font-medium text-blue-700 align-middle">
          forfait d’essai
        </span>
        Inscription en forfait d’essai ; sur un groupe, le nombre d’essais.
      </span>
      <span className="basis-full">
        L’effectif ne compte que les inscriptions actives : une inscription inactive reste
        rattachée à son créneau pour l’historique, sans y être comptée.
      </span>
    </p>
  );
}

// Le tag « reserve », declare une fois : la carte l'affiche a deux
// endroits — a cote du compteur, ou a cote du nom de l'eleve — et deux
// copies divergeraient au premier ajustement.
const TAG_RESERVE =
  'shrink-0 rounded border border-blue-400 bg-white px-1 text-[10px] font-medium leading-tight text-blue-700';
// Le forfait d'essai, meme dessin (2026-09-29) : sur un cours individuel
// il accompagne le nom de l'eleve, sur un groupe il compte les essais.
const TAG_ESSAI = TAG_RESERVE;

function CarteCreneau({
  creneau,
  regroupement,
  enConflit,
  onModifier,
  presences = false,
}: {
  creneau: Creneau;
  regroupement: Regroupement;
  /** Ce creneau chevauche un autre : meme encadrant ou meme salle. */
  enConflit: boolean;
  onModifier?: () => void;
  /** Vrai : l'icone de la feuille de presence, a gauche du crayon. */
  presences?: boolean;
}) {
  /**
   * QUATRE ETATS EXCLUSIFS, dans cet ordre (code couleur du 2026-09-02) :
   *
   *   - ROUGE  : le creneau en chevauche un autre. C'est le seul defaut
   *              qui empeche le cours d'avoir lieu — il passe devant ;
   *   - JAUNE  : au-dela de sa capacite ;
   *   - VERT   : de la place reste, du creneau vide au presque plein.
   *              C'est ce qu'on cherche des yeux quand une famille
   *              demande un horaire ;
   *   - BLANC  : a sa capacite, rien a signaler.
   *
   * Une capacite inconnue ne se juge pas : le creneau reste blanc plutot
   * que d'etre annonce disponible sans qu'on en sache rien.
   */
  const sature = creneau.capacite_max !== null && creneau.effectif > creneau.capacite_max;
  const disponible = creneau.capacite_max !== null && creneau.effectif < creneau.capacite_max;

  /**
   * TAG « RESERVE » : la place est prise, mais rien n'a ete regle.
   *
   * Restreint au COURS INDIVIDUEL — l'indicateur du type d'activite
   * (2026-09-30), ni la capacite a 1 ni « une seule inscription » : un atelier de groupe qui n'a qu'un
   * inscrit reste un groupe (meme critere que l'ecran des elements de
   * paie). C'est ce qui donne son sens au mot : quelqu'un tient le
   * creneau sans l'avoir paye.
   *
   * « Aucun paiement » se lit sur la PRESENCE d'une ligne de ventilation,
   * pas sur un solde : le serveur compte les inscriptions qu'aucun
   * reglement ne couvre.
   */
  const reserve = creneau.individuel && creneau.effectif_sans_paiement > 0;

  /**
   * COURS INDIVIDUEL PLEIN : on nomme l'eleve plutot que de compter.
   *
   * « 1/1 insc. » ne dit rien qu'on ne voie deja a la couleur de la
   * carte. Le nom, lui, repond a la question qu'on se pose devant un
   * planning : qui a ce creneau ?
   *
   * Restreint aux cours individuels (indicateur du type, 2026-09-30) —
   * le meme critere que le tag « reserve »,
   * et le seul ou un nom unique a un sens. Un cours de groupe garde son
   * compteur, meme s'il n'a qu'un inscrit. Le serveur ne rend le nom que
   * s'il y a exactement une inscription active, et seulement a qui a le
   * droit de lire les personnes : les deux gardes tiennent sans que la
   * carte ait a les rejouer.
   */
  const eleve =
    creneau.individuel && creneau.adherent_prenom && creneau.adherent_nom
      ? `${creneau.adherent_prenom} ${creneau.adherent_nom.toUpperCase()}`
      : null;

  // « Atelier · Professeur », ampute de ce que la colonne porte deja.
  // Un atelier tenu a deux le montre : « Chorale · Marine + Orso ».
  const encadrants = creneau.encadrants.map((e) => e.prenom_usuel).join(' + ');
  // La colonne « par professeur » porte deja le titulaire : la carte n'y
  // repete que les co-encadrants.
  const coEncadrants = creneau.encadrants
    .slice(1)
    .map((e) => e.prenom_usuel)
    .join(' + ');

  const identite = [
    regroupement !== 'activite' ? creneau.activite_libelle : null,
    regroupement !== 'professeur' ? encadrants || null : coEncadrants || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      // Hauteur minimale commune : une carte sans professeur ni lieu
      // n'a que deux lignes, et la grille prenait un aspect d'escalier.
      className={`group relative flex min-h-[3.75rem] flex-col justify-center rounded border px-2 py-1.5 text-xs transition hover:border-encre-400 ${
        enConflit
          ? 'border-red-400 bg-red-50'
          : sature
            ? 'border-yellow-400 bg-yellow-50'
            : disponible
              ? 'border-emerald-400 bg-emerald-50'
              : 'border-encre-200 bg-white'
      }`}
    >
      {/* Toujours visible : un contrôle qui n'apparaît qu'au survol est un
          contrôle que personne ne trouve. */}
      {(onModifier || presences) && (
        <div className="absolute right-1 top-1 flex gap-1">
          {presences && (
            <Infobulle texte="Feuille de présence" className="inline-flex">
              <Link
                to={`/creneaux/${creneau.id}/presences`}
                className="inline-flex items-center rounded border border-encre-200 bg-white px-1.5 py-0.5 leading-none text-encre-500 transition hover:border-encre-400 hover:text-encre-900"
                aria-label="Feuille de présence"
              >
                <IconePresences />
              </Link>
            </Infobulle>
          )}
          {onModifier && (
            <Infobulle texte="Modifier ce créneau" className="inline-flex">
              <button
                type="button"
                className="rounded border border-encre-200 bg-white px-1.5 leading-none text-encre-500 transition hover:border-encre-400 hover:text-encre-900"
                aria-label="Modifier ce créneau"
                onClick={onModifier}
              >
                ✎
              </button>
            </Infobulle>
          )}
        </div>
      )}
      {/* L'annee voyage avec le creneau : la liste d'inscriptions se cale
          sur l'annee ACTIVE par defaut, et un creneau de 2026-27 filtre
          sur 2025-26 ne rendrait rien. */}
      <Infobulle
        balise="div"
        texte={`${formaterPlage(creneau.heure_debut, creneau.heure_fin)} — ${identite || creneau.activite_libelle}`}
      >
      <Link
        to={`/inscriptions?creneauId=${creneau.id}&anneeId=${creneau.annee_id}`}
        className="block leading-tight"
      >
        {/* L'heure de FIN quitte la carte : elle se deduit de la duree, et
            la grille est deja ordonnee par heure de debut. L'infobulle la
            porte encore, pour qui la cherche. */}
        <div className="tabulaire font-medium text-encre-900">
          {formaterHeure(creneau.heure_debut)}{' '}
          <span className="font-normal text-encre-500">
            ({formaterDuree(creneau.duree_minutes)})
          </span>
        </div>

        {/* La colonne porte déjà la dimension du regroupement : on ne la
            répète pas dans chaque carte. `professeur_nom` est le prénom
            d'usage — c'est la seule identité que porte le classeur. */}
        {identite && <div className="truncate text-encre-700">{identite}</div>}

        {/* Un cours individuel plein NOMME son eleve ; tous les autres
            gardent leur compteur, au caractere pres — d'ou deux branches
            plutot qu'une mise en page commune : passer le pied en `flex`
            pour tout le monde aurait deplace les espaces des cartes de
            groupe, qui ne changent pas.

            Cote nom, le rang `flex` fait tout le travail : le nom porte
            `flex-1 truncate` et cede la place aux elements a largeur
            fixe, donc il se coupe par « … » exactement de ce qu'il faut
            quand le tag « reserve » ou le lieu s'ajoutent. La tuile, elle,
            ne bouge pas d'un pixel. `min-w-0` est indispensable : sans
            lui un enfant flex refuse de descendre sous sa largeur de
            contenu, et le nom deborderait au lieu de se couper. */}
        <div className="text-encre-500">
          {eleve ? (
            <span className="flex items-baseline gap-1.5">
              <Infobulle texte={eleve} className="min-w-0 flex-1 truncate text-encre-700">
                {eleve}
              </Infobulle>
              {creneau.effectif_essai > 0 && <span className={TAG_ESSAI}>forfait d’essai</span>}
              {reserve && <span className={TAG_RESERVE}>réservé</span>}
              {regroupement !== 'lieu' && creneau.lieu_code && (
                <span className="shrink-0 text-encre-400">· {creneau.lieu_code}</span>
              )}
            </span>
          ) : (
            <>
              <span className="tabulaire">
                {creneau.effectif}
                {creneau.capacite_max !== null && `/${creneau.capacite_max}`} insc.
              </span>
              {/* Un TAG plutot qu'une couleur de fond : le non-regle se
                  superpose a l'etat du creneau au lieu de le remplacer. */}
              {creneau.effectif_essai > 0 && (
                <span className={`ml-1.5 ${TAG_ESSAI}`}>
                  {creneau.effectif_essai === 1 && creneau.effectif === 1
                    ? 'forfait d’essai'
                    : `${creneau.effectif_essai} en essai`}
                </span>
              )}
              {reserve && <span className={`ml-1.5 ${TAG_RESERVE}`}>réservé</span>}
              {/* Le lieu ne mérite plus sa ligne, mais le planning sans salle
                  ne se lit pas sur place. */}
              {regroupement !== 'lieu' && creneau.lieu_code && (
                <span className="text-encre-400"> · {creneau.lieu_code}</span>
              )}
            </>
          )}
        </div>
      </Link>
      </Infobulle>
    </div>
  );
}
